import { Grid, TextField } from '@mui/material'
import { ChangeEvent, useEffect, useState } from 'react'
import {
  UseFormRegister,
  FieldValues,
  Control,
  Controller,
} from 'react-hook-form'

interface DateSearchProps {
  register: UseFormRegister<FieldValues>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any>
  dateFromLabel: string
  dateToLabel: string
}

function yearToUtcIso(year: number, snap: 'start' | 'end'): string {
  const date =
    snap === 'end'
      ? new Date(Date.UTC(year, 11, 31))
      : new Date(Date.UTC(year, 0, 1))
  return date.toISOString()
}

/** Accept Date, ISO string, or "2013" — filter sync JSON-stringifies Dates. */
export function parseYearValue(value: unknown): number | null {
  if (value instanceof Date && !isNaN(value.getTime())) {
    // Date objects from the old picker are local calendar dates.
    const year = value.getFullYear()
    return year >= 1000 ? year : null
  }
  if (typeof value === 'number' && Number.isInteger(value) && value >= 1000) {
    return value
  }
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  if (/^\d{4}$/.test(trimmed)) return Number(trimmed)
  const parsed = new Date(trimmed)
  if (isNaN(parsed.getTime())) return null
  const year = parsed.getUTCFullYear()
  return year >= 1000 ? year : null
}

function yearToInput(value: unknown): string {
  const year = parseYearValue(value)
  return year == null ? '' : String(year)
}

function YearSearchField({
  label,
  value,
  onChange,
  snap,
}: {
  label: string
  value: unknown
  onChange: (date: string | null) => void
  snap: 'start' | 'end'
}) {
  const [input, setInput] = useState(() => yearToInput(value))
  const [focused, setFocused] = useState(false)
  const committedYear = parseYearValue(value)

  useEffect(() => {
    if (focused) return
    setInput(yearToInput(value))
  }, [value, focused])

  const handleTyping = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value.replace(/\D/g, '').slice(0, 4)
    setInput(raw)
    if (raw.length === 4) {
      const year = Number(raw)
      // Date.UTC maps 0000–0099 to 1900–1999; skip years we cannot display.
      if (year >= 1000) {
        onChange(yearToUtcIso(year, snap))
      }
      return
    }
    if (committedYear != null) {
      onChange(null)
    }
  }

  return (
    <TextField
      label={label}
      fullWidth
      size='small'
      value={input}
      onChange={handleTyping}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      error={false}
      inputProps={{
        inputMode: 'numeric',
        maxLength: 4,
      }}
    />
  )
}

export function DateSearch({
  register,
  control,
  dateFromLabel,
  dateToLabel,
}: DateSearchProps) {
  void register

  return (
    <Grid container spacing={0.5}>
      <Grid item xs={6} md={6}>
        <Controller
          control={control}
          name='dateFrom'
          render={({ field: { onChange, value } }) => (
            <YearSearchField
              label={dateFromLabel}
              value={value}
              onChange={onChange}
              snap='start'
            />
          )}
        />
      </Grid>

      <Grid item xs={6} md={6}>
        <Controller
          control={control}
          name='dateTo'
          render={({ field: { onChange, value } }) => (
            <YearSearchField
              label={dateToLabel}
              value={value}
              onChange={onChange}
              snap='end'
            />
          )}
        />
      </Grid>
    </Grid>
  )
}
